import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { fetchEventsWithCache, getCachedEvents } from './FetchData';
import { PerformerAvatar, Lightbox } from './PerformerAvatar';

const PerformersTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState('');
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      const cached = getCachedEvents();
      if (cached) {
        setEvents(cached);
        return;
      }

      setLoading(true);
      setApiMessage('Please hold, respectfully calling the Comedy Cellar API.');

      try {
        const allEvents = await fetchEventsWithCache();
        setEvents(allEvents);
        setApiMessage('');
      } catch (error) {
        console.error('Error fetching events:', error);
        setApiMessage('Error fetching events. Please try again later.');
      }

      setLoading(false);
    };

    fetchEvents();
  }, []);

  const performers = useMemo(() => {
    const map = new Map();
    events.forEach(event => {
      (event.performers || []).forEach(p => {
        const name = (p.name || '').trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (!map.has(key)) {
          map.set(key, { name, description: p.description || '', website: p.website, image: p.image, appearances: [] });
        }
        const entry = map.get(key);
        if (!entry.description && p.description) entry.description = p.description;
        if (!entry.website && p.website) entry.website = p.website;
        if (!entry.image && p.image) entry.image = p.image;
        entry.appearances.push({ date: event.date, timestamp: event.timestamp, title: event.title, showId: event.id });
      });
    });
    return [...map.values()]
      .map(p => ({ ...p, appearances: p.appearances.sort((a, b) => a.timestamp.localeCompare(b.timestamp)) }))
      .sort((a, b) => b.appearances.length - a.appearances.length || a.name.localeCompare(b.name));
  }, [events]);

  const filteredPerformers = useMemo(() => {
    const normalizedFilter = filter.trim().toLowerCase();
    if (!normalizedFilter) return performers;
    return performers.filter(p => p.name.toLowerCase().includes(normalizedFilter));
  }, [performers, filter]);

  const toggleExpanded = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const hasValidWebsite = (website) => typeof website === 'string' && /^https?:\/\//i.test(website);

  return (
    <div className="p-4 flex flex-col items-center justify-center fadeIn">
      {loading ? (
        <p className="text-center">{apiMessage}</p>
      ) : apiMessage ? (
        <p className="text-center">{apiMessage}</p>
      ) : (
        <div className="w-full max-w-4xl slideIn">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter performers..."
            className="p-2 border border-border-color rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color bg-bg-color text-text-color w-full max-w-md mb-2"
          />
          <p className="text-sm mb-4" style={{ color: 'var(--secondary-color)' }}>
            {filteredPerformers.length} performer{filteredPerformers.length === 1 ? '' : 's'} over the next 14 days
          </p>

          {performers.length === 0 ? (
            <p className="text-center" style={{ color: 'var(--secondary-color)' }}>
              No performers found for the next 14 days.
            </p>
          ) : filteredPerformers.length === 0 ? (
            <p className="text-center" style={{ color: 'var(--secondary-color)' }}>
              No performers match your filter.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 slideIn w-full">
              {filteredPerformers.map((performer) => {
                const key = performer.name.toLowerCase();
                const isExpanded = !!expanded[key];
                const firstAppearance = performer.appearances[0];
                const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(`${performer.name} comedian`)}`;
                const youtubeUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(`${performer.name} standup`)}`;
                const instagramUrl = `https://www.google.com/search?q=${encodeURIComponent(`${performer.name} instagram`)}`;

                return (
                  <div key={key} className="bg-bg-color p-4 rounded-lg shadow-md border border-border-color collapsible-container">
                    <div className="cursor-pointer" onClick={() => toggleExpanded(key)}>
                      <div className="flex items-center gap-3">
                        <PerformerAvatar
                          name={performer.name}
                          image={performer.image}
                          onEnlarge={() => setLightbox({ name: performer.name, image: performer.image })}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{performer.name}</div>
                          <div className="text-xs" style={{ color: 'var(--secondary-color)' }}>
                            {performer.appearances.length} show{performer.appearances.length === 1 ? '' : 's'}
                          </div>
                        </div>
                      </div>

                      {performer.description && (
                        <p
                          className="text-sm mt-2"
                          style={{
                            color: 'var(--secondary-color)',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {performer.description}
                        </p>
                      )}

                      {!isExpanded && firstAppearance && (
                        <p className="text-xs mt-2" style={{ color: 'var(--secondary-color)' }}>
                          Next: {format(parseISO(firstAppearance.timestamp), 'EEE, MMM d · h:mm a')}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                      <a
                        href={googleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-accent-color text-white px-3 py-2 rounded-md text-sm hover:bg-opacity-75"
                      >
                        Google
                      </a>
                      <a
                        href={youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-accent-color text-white px-3 py-2 rounded-md text-sm hover:bg-opacity-75"
                      >
                        YouTube
                      </a>
                      <a
                        href={instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-accent-color text-white px-3 py-2 rounded-md text-sm hover:bg-opacity-75"
                      >
                        Instagram
                      </a>
                      {hasValidWebsite(performer.website) && (
                        <a
                          href={performer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-accent-color text-white px-3 py-2 rounded-md text-sm hover:bg-opacity-75"
                        >
                          Website
                        </a>
                      )}
                    </div>

                    <div className={`collapsible-content ${isExpanded ? 'expanded' : ''}`}>
                      <div className="mt-3 pt-3 border-t border-border-color">
                        <h4 className="text-sm font-semibold mb-1">Appearances:</h4>
                        <ul className="text-sm" style={{ color: 'var(--secondary-color)' }}>
                          {performer.appearances.map((appearance, idx) => (
                            <li key={idx}>
                              {format(parseISO(appearance.timestamp), 'EEE, MMM d · h:mm a')} · {appearance.title}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {lightbox && (
        <Lightbox
          name={lightbox.name}
          image={lightbox.image}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

export default PerformersTab;
